import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Tag, ArrowRight, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { offers } from '../data/mock';

const ITEMS_PER_PAGE = 4;

const Offers = () => {
  const [currentPage, setCurrentPage] = useState(1);
  
  const featuredOffers = offers.filter(offer => offer.featured);
  const regularOffers = offers.filter(offer => !offer.featured);
  
  // Pagination for all offers combined
  const allOffers = offers;
  const totalPages = Math.ceil(allOffers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedOffers = allOffers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  
  const paginatedFeatured = paginatedOffers.filter(offer => offer.featured);
  const paginatedRegular = paginatedOffers.filter(offer => !offer.featured);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  return (
    <div className="pt-24 md:pt-28">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-600 via-pink-400 to-amber-700 py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <Sparkles className="w-8 h-8 mr-3" />
            <span className="text-lg font-semibold tracking-wide uppercase">
              Exclusive Deals
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Special Offers & New Launches
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Discover our latest promotions, seasonal packages, and limited-time offers 
            designed to bring you premium beauty experiences at exceptional value
          </p>
        </div>
      </section>

      {/* Featured Offers */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Featured <span className="text-amber-600">This Month</span>
            </h2>
            <p className="text-xl text-gray-600">
              Don't miss out on our handpicked exclusive offers
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {paginatedFeatured.map((offer) => (
              <div
                key={offer.id}
                className="relative overflow-hidden rounded-3xl shadow-2xl group"
              >
                <div className="aspect-[16/10] relative">
                  <img
                    src={offer.image}
                    alt={offer.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                </div>
                
                <div className="absolute top-6 right-6">
                  <div className="bg-amber-600 text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg">
                    {offer.discount}
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-4">
                    <Tag className="w-4 h-4 mr-2" />
                    Featured Offer
                  </div>
                  <h3 className="text-3xl font-bold mb-3">{offer.title}</h3>
                  <p className="text-lg text-gray-200 mb-4">{offer.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>Valid until {offer.validUntil}</span>
                    </div>
                    <Link
                      to="/booking"
                      className="inline-flex items-center px-6 py-3 bg-white text-amber-600 rounded-full hover:bg-gray-100 transition-all duration-300 font-semibold"
                    >
                      Book Now
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Offers */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              More <span className="text-amber-600">Amazing Deals</span>
            </h2>
            <p className="text-xl text-gray-600">
              Explore our complete collection of special offers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {paginatedRegular.map((offer) => (
              <div
                key={offer.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
              >
                <div className="aspect-[16/9] relative overflow-hidden">
                  <img
                    src={offer.image}
                    alt={offer.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-4 right-4">
                    <div className="bg-amber-600 text-white px-5 py-2 rounded-full font-bold shadow-lg">
                      {offer.discount}
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {offer.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{offer.description}</p>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>{offer.validUntil}</span>
                    </div>
                    <Link
                      to="/booking"
                      className="inline-flex items-center text-amber-600 hover:text-amber-700 font-semibold"
                    >
                      Book Now
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12" data-testid="offers-pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                data-testid="offers-pagination-prev"
                className={`flex items-center gap-1 px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-amber-600 hover:text-white'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                Prev
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  data-testid={`offers-pagination-page-${page}`}
                  className={`w-10 h-10 rounded-full font-medium transition-all duration-300 ${
                    currentPage === page
                      ? 'bg-amber-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                data-testid="offers-pagination-next"
                className={`flex items-center gap-1 px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-amber-600 hover:text-white'
                }`}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Results count */}
          {allOffers.length > 0 && (
            <div className="text-center mt-6 text-gray-500">
              Showing {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, allOffers.length)} of {allOffers.length} offers
            </div>
          )}
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-12 text-center text-white">
            <Sparkles className="w-12 h-12 text-amber-600 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              New Launches Coming Soon!
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Stay tuned for exciting new services and exclusive seasonal packages. 
              Follow us on Instagram to be the first to know!
            </p>
            <a
              href="https://instagram.com/poshsignature"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Follow Us on Instagram
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Terms Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 shadow-md">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Terms & Conditions</h3>
            <ul className="space-y-2 text-gray-600 text-sm">
              <li>• All offers are subject to availability and may be modified or discontinued without prior notice</li>
              <li>• Offers cannot be combined with other promotions or discounts</li>
              <li>• Advanced booking is recommended to secure your preferred time slot</li>
              <li>• Cancellations must be made at least 24 hours in advance</li>
              <li>• All services are provided exclusively for women in our women-only salon</li>
              <li>• Payment is accepted at the salon after service completion</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Offers;
